import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { DatePicker as AntdDatePicker } from 'antd';

import { colors } from '../../styles/colors/';

const StyledDatePicker = styled(AntdDatePicker)`
  width: ${(props) => props.width || '100%'};
  border: ${(props) =>
    !props.disabled && props.error
      ? `1px solid ${colors['red']['5']} !important`
      : `1px solid ${colors['gray']['3']} !important`};
  border-radius: 3px;

  .ant-input {
    font-size: ${(props) => (props.isMobile ? '16px !important' : '14px !important')};
    height: ${(props) => (props.isMobile ? '40px' : '32px')};
  }

  &:hover {
    // If disabled, gray border
    // if error, red border
    // if no error, indigo border
    box-shadow: none;
    border-color: ${(props) =>
      props.disabled
        ? `${colors['gray']['3']} !important`
        : props.error
        ? `${colors['red']['5']} !important`
        : `${colors['indigo']['7']} !important`};
  }

  &:focus {
    // If disabled, no border shadow and gray border
    // if error, red border shadow and red border
    // if no error, indigo border shadow and indigo border
    box-shadow: ${(props) =>
      props.disabled
        ? 'none'
        : props.error
        ? `0px 0px 4px ${colors['red']['7']} !important`
        : `0px 0px 4px ${colors['indigo']['3']} !important`};
    border-color: ${(props) =>
      props.disabled
        ? `${colors['gray']['3']} !important`
        : props.error
        ? `${colors['red']['5']} !important`
        : `${colors['indigo']['7']} !important`};
  }

  &:active {
    // If disabled, no border shadow and gray border
    // if error, red border shadow and red border
    // if no error, indigo border shadow and indigo border
    box-shadow: ${(props) =>
      props.disabled
        ? 'none'
        : props.error
        ? `0px 0px 4px ${colors['red']['7']}`
        : `0px 0px 4px ${colors['indigo']['3']}`};
    border-color: ${(props) =>
      props.disabled
        ? colors['gray']['3']
        : props.error
        ? colors['red']['5']
        : colors['indigo']['7']};
  }

  &:focus-within {
    // If disabled, no border shadow and gray border
    // if error, red border shadow and red border
    // if no error, indigo border shadow and indigo border
    box-shadow: ${(props) =>
      props.disabled
        ? 'none'
        : props.error
        ? `0px 0px 4px ${colors['red']['7']}`
        : `0px 0px 4px ${colors['indigo']['3']}`};
    border-color: ${(props) =>
      props.disabled
        ? colors['gray']['3']
        : props.error
        ? colors['red']['5']
        : colors['indigo']['7']};
  }
`;

/**
 * Primary UI component for user interaction
 */
const DatePicker = ({ width, disabled, error, isMobile, ...props }) => {
  // The calendar component lives outside of the AntdDatePicker, so wrapping the component will not work

  const DatePickerCSS = (
    <style>{css`
      .ant-calendar {
        font-size: ${(props) => (props.isMobile ? '16px' : '14px')};
      }

      .ant-calendar-date {
        color: ${colors['gray']['9']};
      }
      .ant-calendar-last-month-cell .ant-calendar-date,
      .ant-calendar-next-month-btn-day .ant-calendar-date {
        color: ${colors['gray']['6']};
      }

      .ant-calendar-today .ant-calendar-date {
        font-weight: normal;
        color: ${colors['gray']['9']};
        border-color: ${colors['aqua']['3']};
      }

      .ant-calendar-selected-date .ant-calendar-date {
        background: ${colors['aqua']['3']};
        color: ${colors['gray']['9']};
        border: 1px solid transparent;
      }
      .ant-calendar-picker:hover .ant-calendar-picker-input:not(.ant-input-disabled) {
        border-color: transparent;
        border-radius: none;
      }

      /* 
      Careful not to modify .ant-input too much as modifying for date picker
      and using regular input might overwrite styling for the input as well.
    */
      .ant-input:focus {
        border-color: transparent;
        box-shadow: none;
      }

      .ant-input[disabled]:hover {
        border-color: transparent;
      }

      .ant-input {
        border-color: transparent;
        border-radius: 3px;
      }
    `}</style>
  );

  return (
    <React.Fragment>
      {DatePickerCSS}
      <StyledDatePicker
        width={width}
        disabled={disabled}
        error={error}
        isMobile={isMobile}
        {...props}
      />
    </React.Fragment>
  );
};

DatePicker.propTypes = {
  /**
   * DatePicker contents
   */
  width: PropTypes.string,

  disabled: PropTypes.bool,

  error: PropTypes.bool,

  isMobile: PropTypes.bool,
};

DatePicker.defaultProps = {
  width: '100%',
  disabled: false,
  error: false,
  isMobile: false,
};

export { DatePicker };
